import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { User } from 'firebase/auth';
import { 
  Sparkles, 
  Clock, 
  MapPin, 
  ShieldCheck, 
  AlertCircle, 
  CheckCircle2, 
  Loader2, 
  Send, 
  Zap, 
  FileText,
  Calendar,
  Globe,
  Info
} from 'lucide-react';
import { BrandProfile } from '../types';

interface TrendsManagerProps {
  brand: BrandProfile | null;
  onSaveBrand: (data: Partial<BrandProfile>, skipRedirect?: boolean) => Promise<void>;
  isSavingBrand: boolean;
  user: User | null;
  setToast: (t: { message: string, type: 'success' | 'error' } | null) => void;
  isTrial: boolean;
  handleUpgradeFeature: (feature: string) => void;
  isBrazil: boolean;
}

export const TrendsManager = ({
  brand,
  onSaveBrand,
  isSavingBrand,
  user,
  setToast,
  isTrial,
  handleUpgradeFeature,
  isBrazil
}: TrendsManagerProps) => {
  const isOwner = brand?.ownerId === user?.uid || brand?.email?.toLowerCase() === user?.email?.toLowerCase() || !brand;
  const isTester = user?.email?.toLowerCase() === 'dewaldjordaan5@gmail.com' || user?.email?.toLowerCase() === 'dewald.jordan@sparkpr.com';

  // Config State
  const [enableSmartTrends, setEnableSmartTrends] = useState<boolean>(brand?.enableSmartTrends || false);
  const [smartTrendsTime, setSmartTrendsTime] = useState<string>(brand?.smartTrendsTime || '08:00');
  const [location, setLocation] = useState<string>(brand?.location || '');
  const [strategicLens, setStrategicLens] = useState<string>(brand?.strategicLens || '');
  
  const [savingSettings, setSavingSettings] = useState<boolean>(false);
  
  // Scans history State
  const [latestTrendData, setLatestTrendData] = useState<any>(null);
  const [loadingTrends, setLoadingTrends] = useState<boolean>(false);
  const [isManualScanning, setIsManualScanning] = useState<boolean>(false);

  // Sync state if brand changes
  useEffect(() => {
    if (brand) {
      setEnableSmartTrends(brand.enableSmartTrends || false);
      setSmartTrendsTime(brand.smartTrendsTime || '08:00');
      setLocation(brand.location || '');
      setStrategicLens(brand.strategicLens || '');
    }
  }, [brand]);

  // Fetch trend history
  const fetchTrendHistory = async () => {
    if (!brand?.id) return;
    setLoadingTrends(true);
    try {
      const trendsSnap = await getDocs(query(
        collection(db, "trend_scans"),
        where("brandId", "==", brand.id),
        orderBy("date", "desc"),
        limit(5)
      ));
      if (!trendsSnap.empty) {
        setLatestTrendData(trendsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      } else {
        setLatestTrendData([]);
      }
    } catch (err) {
      console.error("Failed to fetch trends in trends tab:", err);
    } finally {
      setLoadingTrends(false);
    }
  };

  useEffect(() => {
    fetchTrendHistory();
  }, [brand?.id]);

  const handleSaveSettings = async () => {
    if (!brand?.id) return;
    setSavingSettings(true);
    try {
      await onSaveBrand({
        enableSmartTrends,
        smartTrendsTime,
        location,
        strategicLens
      }, true);
      setToast({ message: isBrazil ? "Configurações de estratégia salvas com sucesso!" : "Strategy settings saved successfully!", type: 'success' });
    } catch (err: any) {
      console.error("Failed to save brand trends config:", err);
      setToast({ message: isBrazil ? `Erro ao salvar: ${err.message}` : `Failed to save: ${err.message}`, type: 'error' });
    } finally {
      setSavingSettings(false);
    }
  };

  const handleManualScan = async () => {
    if (!brand?.id || !user) return;
    setIsManualScanning(true);
    try {
      const token = await user.getIdToken();
      const response = await fetch('/api/admin/run-smart-trends', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ brandId: brand.id })
      });
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || 'Scan failed');
      }
      setToast({ message: isBrazil ? "Varredura iniciada! Verifique seu e-mail em breve." : "Daily trend scan completed! Check your email.", type: 'success' });
      // Refresh scanned history
      setTimeout(() => fetchTrendHistory(), 3000);
    } catch (err: any) {
      setToast({ message: (isBrazil ? "Falha na varredura: " : "Scan failed: ") + err.message, type: 'error' });
    } finally {
      setIsManualScanning(false);
    }
  };

  if (!brand) {
    return (
      <div className="bg-white p-12 rounded-3xl border border-gray-100 text-center max-w-xl mx-auto shadow-sm">
        <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
        <h3 className="text-lg font-bold text-gray-900 mb-2">
          {isBrazil ? "Nenhuma Marca Ativa" : "No Active Brand Selected"}
        </h3>
        <p className="text-sm text-gray-500">
          {isBrazil 
            ? "Por favor, crie ou selecione um perfil de marca para gerenciar Tendências e Foco Estratégico." 
            : "Please select or create a brand profile to manage Trends and Strategic Focus."}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <header className="flex flex-col gap-2">
        <h2 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
          <Zap className="w-8 h-8 text-indigo-600 shrink-0" />
          <span>{isBrazil ? "Tendências e Foco Estratégico" : "Trends & Strategic Focus"}</span>
        </h2>
        <p className="text-gray-500 text-sm max-w-2xl leading-relaxed">
          {isBrazil 
            ? "Alinhe sua estratégia de marca com tópicos de tendências diárias do seu mercado, permitindo planos de conteúdo inteligentes e de alto impacto de maneira automatizada."
            : "Align your brand strategy with daily trending topics and industry shifts, enabling automated, high-relevance focus when creating content plans."}
        </p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        {/* Left: Setup Fields */}
        <div className="lg:col-span-7 space-y-6">
          
          {/* Card 1: Strategic Target Lens */}
          <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm space-y-5">
            <div className="flex items-center gap-3 border-b border-gray-50 pb-3">
              <div className="p-2 bg-indigo-50 rounded-xl text-indigo-600">
                <Globe className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900">
                  {isBrazil ? "Lente Estratégica & Filtros" : "Strategic Focus Instructions"}
                </h3>
                <p className="text-xs text-gray-400">
                  {isBrazil ? "Orientações enviadas para a IA na hora de buscar tendências" : "Guides the AI when scanning trend news and generating content plans"}
                </p>
              </div>
            </div>

            <div className="space-y-4">
              {/* Target Location */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <MapPin className="w-4 h-4 text-indigo-500" />
                  <label className="text-xs font-bold text-gray-700">
                    {isBrazil ? "Geolocalização / Mercado-Alvo" : "Target Market / Location"}
                  </label>
                </div>
                <input
                  type="text"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder={isBrazil ? "Ex: Brasil, Mercado cooling e industrial" : "e.g. South Africa, Industrial sector"}
                  className="w-full text-sm font-bold bg-gray-50 border border-gray-100 rounded-xl px-4 py-3 outline-none text-gray-900 focus:bg-white focus:ring-2 focus:ring-indigo-500 placeholder:text-gray-400 transition-all"
                />
                <p className="text-[10px] text-gray-400 mt-1 uppercase font-bold">
                  {isBrazil 
                    ? "Isso nos ajuda a garantir que as ideias falem à região geográfica correta." 
                    : "Ensures generated social post concepts speak to your audience's local geographic and climatic reality."}
                </p>
              </div>

              {/* Strategic Instruction Input / Strategic Lens */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <ShieldCheck className="w-4 h-4 text-indigo-500" />
                  <label className="text-xs font-bold text-gray-700">
                    {isBrazil ? "Instruções Curtas de Busca / Lente Estratégica" : "Trend Search Instruction / Strategic Lens"}
                  </label>
                </div>
                <textarea
                  value={strategicLens}
                  onChange={(e) => setStrategicLens(e.target.value)}
                  placeholder={isBrazil ? "Como você quer que relacionemos as notícias ao seu negócio? Ex: buscar por 'El nino'" : "e.g. find trend: El Niño. Analyze how climatic changes affect ventilation/air blast product line."}
                  rows={4}
                  className="w-full text-sm font-bold bg-gray-50 border border-gray-100 rounded-xl px-4 py-3 outline-none text-gray-900 focus:bg-white focus:ring-2 focus:ring-indigo-500 resize-none placeholder:text-gray-400 transition-all"
                />
                <p className="text-[10px] text-gray-400 mt-1 leading-relaxed">
                  {isBrazil 
                    ? "Escreva um termo ou diretriz de busca. A IA usará isso para peneirar novos fenômenos e relacioná-los ao contexto do seu nicho."
                    : "Specify search guidelines or business filters. The AI relates these trends strictly to your business units (e.g. Ventura Cooling's specialized product lines vs drought)."}
                </p>
              </div>
            </div>

            <button
              onClick={handleSaveSettings}
              disabled={savingSettings || isSavingBrand}
              className="w-full mt-2 py-3 bg-indigo-600 text-white rounded-xl text-sm font-bold hover:bg-indigo-700 transition-all flex items-center justify-center gap-2"
            >
              {(savingSettings || isSavingBrand) ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>{isBrazil ? "Gravando..." : "Saving..."}</span>
                </>
              ) : (
                <span>{isBrazil ? "Salvar Estratégia de Busca" : "Save Search Strategy"}</span>
              )}
            </button>
          </div>

          {/* Card 2: Daily Report Email Configuration */}
          <div className="bg-gradient-to-br from-indigo-50/50 to-blue-50/50 p-6 rounded-3xl border border-indigo-100 shadow-sm space-y-4 relative overflow-hidden">
            <div className="absolute top-0 right-0 p-4 opacity-5 pointer-events-none">
              <Sparkles className="w-32 h-32 text-indigo-600" />
            </div>

            <div className="relative z-10 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white rounded-xl shadow-xs text-indigo-600">
                  <Zap className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-gray-900">
                    {isBrazil ? "Relatório Diário de Tendências (E-mail)" : "Daily Smart Trends Email"}
                  </h3>
                  <p className="text-[10px] text-indigo-600 font-bold uppercase tracking-wider">AI Add-on</p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                {!enableSmartTrends && !isTester && (
                  <span className="text-xs font-black text-indigo-600 hidden sm:block">+$5 / mo</span>
                )}
                
                <button
                  type="button"
                  onClick={async () => {
                    if (!isOwner) return;
                    
                    if (!enableSmartTrends && !isTester) {
                      handleUpgradeFeature('SMART_TRENDS');
                      return;
                    }
                    
                    const newValue = !enableSmartTrends;
                    setEnableSmartTrends(newValue);
                    
                    // Auto-save setting
                    try {
                      await onSaveBrand({ enableSmartTrends: newValue }, true);
                      setToast({ message: isBrazil ? "E-mail de tendências atualizado!" : "Trends email configuration saved!", type: 'success' });
                    } catch (e) {
                      console.error("Failed to save trends toggle:", e);
                    }
                  }}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${enableSmartTrends ? 'bg-indigo-600' : 'bg-gray-200'} ${!isOwner ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${enableSmartTrends ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>
            </div>

            <p className="text-sm text-gray-600 leading-relaxed max-w-md">
              {isBrazil 
                ? "Nossa IA faz varreduras diárias do seu nicho de acordo com seu foco estratégico local e envia um compilado por e-mail, juntamente com o seu robô agendado."
                : "Our AI scans the internet daily matching your niche and location filters, delivering trend alerts, sugered social content concepts, and calendar reviews every morning."}
            </p>

            {enableSmartTrends && (
              <div className="p-4 bg-white/70 rounded-2xl border border-white space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-indigo-500" />
                    <span className="text-xs font-bold text-gray-700">
                      {isBrazil ? "Hora de Envio Preferida" : "Preferred Report Time"}
                    </span>
                  </div>
                  <select
                    value={smartTrendsTime}
                    onChange={async (e) => {
                      const newTime = e.target.value;
                      setSmartTrendsTime(newTime);
                      try {
                        await onSaveBrand({ smartTrendsTime: newTime }, true);
                        setToast({ message: isBrazil ? "Hora de envio gravada!" : "Report delivery time updated!", type: 'success' });
                      } catch (err) {
                        console.error("Failed to save trend time:", err);
                      }
                    }}
                    className="text-xs font-bold bg-white border border-gray-100 rounded-lg px-2 py-1 outline-none text-indigo-600 focus:ring-1 focus:ring-indigo-500"
                  >
                    {Array.from({ length: 24 }).map((_, i) => {
                      const hour = i.toString().padStart(2, '0');
                      return (
                        <React.Fragment key={hour}>
                          <option value={`${hour}:00`}>{hour}:00 UTC</option>
                          <option value={`${hour}:30`}>{hour}:30 UTC</option>
                        </React.Fragment>
                      );
                    })}
                  </select>
                </div>
                <p className="text-[10px] text-gray-400">
                  {isBrazil 
                    ? "Os relatórios automáticos são disparados uma vez por dia às manhãs no fuso horário configurado."
                    : "Ensures alerts are delivered only once in the morning to prevent inbox noise."}
                </p>
              </div>
            )}

            {isTester && (
              <div className="p-4 bg-white/40 rounded-2xl border border-dashed border-indigo-200 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                <div className="space-y-1">
                  <span className="text-xs font-bold text-indigo-700 block">Tester Access Admin</span>
                  <p className="text-[10px] text-indigo-500 leading-tight">
                    {isBrazil ? "Você possui passe livre. Pode acionar varreduras manuais e receber e-mails imediatamente." : "Bypass payment checks. Click to trigger an immediate trend scan of your filters."}
                  </p>
                </div>
                <button
                  onClick={handleManualScan}
                  disabled={isManualScanning}
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 shrink-0"
                >
                  {isManualScanning ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Send className="w-3.5 h-3.5" />
                  )}
                  <span>{isBrazil ? "Executar Varredura" : "Run Manual Scan"}</span>
                </button>
              </div>
            )}
          </div>

          {/* Pricing Info Card to solve Cost details */}
          <div className="bg-gray-50 border border-gray-100 p-5 rounded-3xl space-y-3">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-widest flex items-center gap-1.5">
              <Info className="w-4 h-4 text-indigo-500 shrink-0" />
              <span>{isBrazil ? "Como afeta as gerações e créditos?" : "Tokens & Plan Costing Information"}</span>
            </h4>
            <div className="text-xs text-gray-600 space-y-2 leading-relaxed">
              <p>
                {isBrazil 
                  ? "Ativar o filtro de tendências ao criar planos usa Inteligência de Busca do Google para garantir dados de mercado reais."
                  : "Activating the trends overlay during content plan generation uses real-time search grounding tools to look up climate conditions, regional reports and industry news."}
              </p>
              <ul className="list-disc list-inside space-y-1 pl-1">
                <li>
                  <strong className="text-gray-900">{isBrazil ? "Gerador Padrão: " : "Standard Plan Generation: "}</strong>
                  {isBrazil ? "Deduz 5 tokens por plano." : "Deducts 5 tokens."}
                </li>
                <li>
                  <strong className="text-gray-900">{isBrazil ? "Com Foco em Tendências (Grounding): " : "With Strategic Trend Overlay: "}</strong>
                  {isBrazil ? "Deduz 5 tokens base + custo do processamento avançado de busca." : "Deducts 5 tokens and overlay surcharge depending on regional sources."}
                </li>
                <li>
                  <strong className="text-gray-900">{isBrazil ? "Refinamentos: " : "Refinements / Tweaks: "}</strong>
                  {isBrazil ? "Deduz somente 1 token por alteração pontual no plano criado." : "Only charges 1 token to update or tweak concepts."}
                </li>
              </ul>
            </div>
          </div>

        </div>

        {/* Right: Scanned Feed stream */}
        <div className="lg:col-span-5 space-y-6">
          <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm space-y-5">
            <header className="flex items-center justify-between border-b border-gray-50 pb-3">
              <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-indigo-500 shrink-0" />
                <span>{isBrazil ? "Tendências Detectadas" : "Latest Scanned Trends"}</span>
              </h3>
              {loadingTrends && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
            </header>

            {loadingTrends && !latestTrendData && (
              <div className="py-20 text-center space-y-3">
                <Loader2 className="w-8 h-8 animate-spin text-indigo-500 mx-auto" />
                <p className="text-xs text-gray-400">{isBrazil ? "Buscando varreduras anteriores..." : "Fetching trend reports..."}</p>
              </div>
            )}

            {!loadingTrends && (!latestTrendData || latestTrendData.length === 0) && (
              <div className="py-12 bg-gray-50 border border-dashed border-gray-200 rounded-2xl text-center px-4">
                <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-sm text-gray-800 font-bold mb-1">{isBrazil ? "Sem Varreduras Recentes" : "No Trends Found Yet"}</p>
                <p className="text-xs text-gray-400 max-w-xs mx-auto">
                  {isBrazil 
                    ? "Sua estratégia de busca e localização servirá para a próxima varredura diária ou geração de plano."
                    : "Register search terms! Newly processed daily trends and business alignments will appear right here."}
                </p>
              </div>
            )}

            {latestTrendData && latestTrendData.length > 0 && (
              <div className="space-y-6">
                {latestTrendData.map((scan: any, idx: number) => (
                  <div key={scan.id || idx} className="space-y-4 border-b border-gray-50 pb-6 last:border-0 last:pb-0">
                    <div className="flex items-center justify-between text-xs text-indigo-500 font-bold">
                      <div className="flex items-center gap-1.5">
                        <Calendar className="w-3.5 h-3.5" />
                        <span>{scan.date}</span>
                      </div>
                      <span className="text-[10px] bg-indigo-50 text-indigo-600 px-2.5 py-0.5 rounded-full uppercase">
                        {idx === 0 ? (isBrazil ? 'Recente' : 'Latest Scan') : (isBrazil ? 'Histórico' : 'Archive')}
                      </span>
                    </div>

                    <div className="space-y-4">
                      {scan.trends && scan.trends.map((t: any, sIdx: number) => (
                        <div key={sIdx} className="bg-gray-50 p-4 rounded-2xl border border-gray-100 space-y-3">
                          <header className="space-y-1">
                            <span className="text-[9px] font-black text-indigo-600 uppercase tracking-widest">{isBrazil ? 'Conceito da Tendência' : 'Trend Concept'}</span>
                            <h4 className="text-sm font-extrabold text-gray-900 leading-snug">
                              {t.concept}
                            </h4>
                          </header>

                          <div className="space-y-2">
                            <p className="text-xs text-gray-700 font-bold leading-relaxed">
                              {t.summary}
                            </p>
                            
                            {t.comment && (
                              <div className="mt-2.5 pt-2.5 border-t border-gray-200/60 leading-relaxed text-xs text-gray-500 font-bold bg-white/40 p-2.5 rounded-xl border border-white">
                                <span className="text-[9px] font-black text-amber-600 uppercase tracking-widest block mb-1">
                                  {isBrazil ? 'Visão Recomendada e Foco (Tensão / Ação)' : 'Expert POV / Strategic Tension'}
                                </span>
                                {t.comment}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
